if (!customElements.get('campaign-speedtempo-event')) {
  const UNLOCK_STORAGE_PREFIX = 'fila:cse-unlock:';

  class CampaignSpeedtempoEvent extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.syncLockedState();
      this.syncCouponAvailability();
      this.syncCouponState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.querySelectorAll('[data-campaign-event-copy]').forEach((button) => {
        window.clearTimeout(button.copyResetTimer);
      });
    }

    get apiBaseUrl() {
      return typeof FILA_API_BASE_URL !== 'undefined' ? FILA_API_BASE_URL : 'https://lkk-store-api.fila.co.kr';
    }

    get isCouponDownloadEnabled() {
      return this.dataset.couponDownloadEnabled !== 'false';
    }

    getAccessToken() {
      try {
        return window.localStorage.getItem('accessToken');
      } catch (error) {
        return null;
      }
    }

    isUnlocked(key) {
      if (!key) return true;
      try {
        return window.localStorage.getItem(UNLOCK_STORAGE_PREFIX + key) === 'true';
      } catch (error) {
        return false;
      }
    }

    unlock(key) {
      if (!key) return;
      try {
        window.localStorage.setItem(UNLOCK_STORAGE_PREFIX + key, 'true');
      } catch (error) {
        // Storage can be unavailable (private mode). Unlock only for this page view.
      }
      this.syncLockedState();
    }

    syncLockedState() {
      this.querySelectorAll('[data-campaign-event-locked-key]').forEach((element) => {
        const unlocked = this.isUnlocked(element.dataset.campaignEventLockedKey);
        element.classList.toggle('is-locked', !unlocked);
        if (unlocked) {
          element.removeAttribute('aria-disabled');
        } else {
          element.setAttribute('aria-disabled', 'true');
        }
      });
    }

    setCouponDownloadedState(button) {
      const label = button.querySelector('[data-campaign-event-button-label]');
      const doneLabel = button.dataset.doneLabel || '쿠폰 다운로드 완료';
      if (label) label.textContent = doneLabel;
      button.setAttribute('aria-label', doneLabel);
      button.classList.add('is-coupon-downloaded');
      button.disabled = true;
    }

    syncCouponAvailability() {
      if (this.isCouponDownloadEnabled) return;

      this.querySelectorAll('[data-campaign-event-coupon]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
    }

    async syncCouponState() {
      if (!this.isCouponDownloadEnabled) return;

      const buttons = Array.from(this.querySelectorAll('[data-campaign-event-coupon]')).filter(
        (button) => button.dataset.cmpgNo
      );
      if (!buttons.length) return;

      const token = this.getAccessToken();
      if (!token) return;

      try {
        const response = await fetch(this.apiBaseUrl + '/api/v1/users/coupons/download/history', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
        const data = await response.json();
        const history = data?.history || [];
        if (!data?.success || !history.length) return;

        buttons.forEach((button) => {
          const downloaded = history.some((item) => String(item.cmpg_no) === String(button.dataset.cmpgNo));
          if (downloaded) this.setCouponDownloadedState(button);
        });
      } catch (error) {
        console.error('[쿠폰 다운로드 이력 조회 실패]', error);
      }
    }

    async handleCouponDownload(button) {
      if (!this.isCouponDownloadEnabled || button.disabled || button.dataset.couponPending === 'true') return;

      if (!this.getAccessToken()) {
        window.location.href = '/account/login?returnUrl=' + encodeURIComponent(window.location.pathname);
        return;
      }

      const cmpgNo = button.dataset.cmpgNo;
      const cmpgBnftNo = button.dataset.cmpgBnftNo;
      if (!cmpgNo || !cmpgBnftNo) {
        window.alert('쿠폰 정보가 설정되지 않았습니다.');
        return;
      }

      const label = button.querySelector('[data-campaign-event-button-label]');
      const originalLabel = label ? label.textContent : '';
      button.dataset.couponPending = 'true';
      button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = button.dataset.loadingLabel || '다운로드 중...';

      try {
        const downloadPath =
          button.dataset.couponDownloadType === 'realtime'
            ? '/api/v1/users/coupons/download-realtime'
            : '/api/v1/users/coupons/download';
        const response = await fetch(this.apiBaseUrl + downloadPath, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + this.getAccessToken(),
          },
          body: JSON.stringify({
            cmpg_no: cmpgNo,
            cmpg_bnft_no: cmpgBnftNo,
          }),
        });
        const data = await response.json();

        if (data?.success === true) {
          window.alert('쿠폰이 다운로드되었습니다.');
          this.setCouponDownloadedState(button);
        } else {
          window.alert(data?.message || data?.detail || '쿠폰 다운로드에 실패했습니다.');
          if (label) label.textContent = originalLabel;
        }
      } catch (error) {
        console.error('[쿠폰 다운로드 실패]', error);
        window.alert('쿠폰 다운로드 중 오류가 발생했습니다.');
        if (label) label.textContent = originalLabel;
      } finally {
        delete button.dataset.couponPending;
        if (!button.disabled) button.removeAttribute('aria-disabled');
      }
    }

    handleClick = async (event) => {
      const locked = event.target.closest('.is-locked');
      if (locked && this.contains(locked)) {
        event.preventDefault();
        const message = locked.dataset.lockedMessage;
        if (message) window.alert(message);
        return;
      }

      const unlocker = event.target.closest('[data-campaign-event-unlock-key]');
      if (unlocker && this.contains(unlocker)) {
        this.unlock(unlocker.dataset.campaignEventUnlockKey);
      }

      const couponButton = event.target.closest('[data-campaign-event-coupon]');
      if (couponButton && this.contains(couponButton)) {
        event.preventDefault();
        await this.handleCouponDownload(couponButton);
        return;
      }

      const button = event.target.closest('[data-campaign-event-copy]');
      if (!button || !this.contains(button)) return;

      const source = button.querySelector('[data-campaign-event-copy-source]');
      const value = source ? source.textContent : '';
      if (!value) return;

      const label = button.querySelector('[data-campaign-event-button-label]');
      const originalLabel = button.dataset.originalLabel || (label ? label.textContent : '');
      const originalAriaLabel = button.dataset.originalAriaLabel || button.getAttribute('aria-label') || originalLabel;
      const copied = await this.copyText(value);
      const feedbackLabel = copied
        ? button.dataset.successLabel || '복사 완료'
        : button.dataset.failureLabel || '복사하지 못했습니다';

      button.dataset.originalLabel = originalLabel;
      button.dataset.originalAriaLabel = originalAriaLabel;
      button.classList.toggle('is-copied', copied);
      button.classList.toggle('is-copy-failed', !copied);
      button.setAttribute('aria-label', feedbackLabel);
      if (label) label.textContent = feedbackLabel;
      this.announce(feedbackLabel);

      window.clearTimeout(button.copyResetTimer);
      button.copyResetTimer = window.setTimeout(() => {
        button.classList.remove('is-copied');
        button.classList.remove('is-copy-failed');
        button.setAttribute('aria-label', originalAriaLabel);
        if (label) label.textContent = originalLabel;
      }, 1800);
    };

    async copyText(value) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(value);
          return true;
        } catch (error) {
          // Permission can be denied inside the theme editor iframe. Use the fallback below.
        }
      }

      return this.copyTextFallback(value);
    }

    copyTextFallback(value) {
      const textarea = document.createElement('textarea');
      const activeElement = document.activeElement;
      const selection = window.getSelection();
      const ranges = [];

      if (selection) {
        for (let index = 0; index < selection.rangeCount; index += 1) {
          ranges.push(selection.getRangeAt(index).cloneRange());
        }
      }

      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);

      try {
        textarea.focus({ preventScroll: true });
        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);
        return document.execCommand('copy');
      } catch (error) {
        return false;
      } finally {
        textarea.remove();
        if (activeElement instanceof HTMLElement) activeElement.focus({ preventScroll: true });
        if (selection && ranges.length) {
          selection.removeAllRanges();
          ranges.forEach((range) => selection.addRange(range));
        }
      }
    }

    announce(message) {
      const status = this.querySelector('[data-campaign-event-status]');
      if (!status) return;

      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = message;
      });
    }
  }

  customElements.define('campaign-speedtempo-event', CampaignSpeedtempoEvent);
}
